import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid";
import styles from "./VendorManagement.module.css";
import useToaster from "../../customHook/useToaster";
import ClearIcon from '@mui/icons-material/Clear';
import { capitalize } from "lodash";
import CustomSelect from "../../CommonComponents/Select/CustomSelect";
import CommonButton from "../../CommonComponents/CommonButton";
import VendorCard from "./VendorCard";
import AddVendorModal from "./AddVendorModal";
import { useDispatch, useSelector } from "react-redux";
import { 
  addVendorLocation, 
  fetchAllVendors, 
  getCitiesByStateVendor, 
  getStateListVendor, 
  initialVendorList, 
  vendorOnboard 
} from "../../Services/vendorManagement.service";
import { secureData } from "../../Encryption/encryption";
import { jwtDecode } from "jwt-decode";
import { isLoadingStateReducer } from "../../Redux/Slice/loginSlice";
import DisplayVendorDetails from "./DisplayVendorDetails";

const initialLocationState = { state: "", stateId: "", city: "" };

const NewVendorManagement = () => {
  const [lobId, setLobId] = useState();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isViewVendorOpen, setIsViewVendorOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(initialLocationState);
  const [errors, setErrors] = useState({});
  const [vendorsData, setVendorsData] = useState([]);
  const [isAddVendorOpen, setIsAddVendorOpen] = useState(false);
  const [locationLabel, setLocationLabel] = useState({})
  const { showToast } = useToaster();
  const dispatch = useDispatch();

  const userRole = useSelector(
    (state) => state?.loginSlice?.userData?.currentRoleName
  );
  const loginToken = useSelector((state) => state?.loginSlice?.userData?.token);
  const permissionList = useMemo(() => {
    if (!loginToken) return [];
    try {
      const decoded = secureData(loginToken, "string_decrypt");
      return jwtDecode(decoded)?.permissions;
    } catch {
      return [];
    }
  }, [loginToken]);

  const hasReadWritePermission = permissionList?.includes("WRITE::VENDOR_MANAGEMENT");

  const employeeId = useMemo(() => {
    if (!loginToken) return null;
    try {
      const decoded = secureData(loginToken, "string_decrypt");
      return jwtDecode(decoded)?.employeeId ?? null;
    } catch {
      return null;
    }
  }, [loginToken]);

  const fetchStates = useCallback(async () => {
    try {
      const res = await getStateListVendor();
      if (res?.success) setStates(res.data);
    } catch (err) {
      showToast(err?.response?.data?.message, "error", true);
    }
  }, []);

  const fetchCities = useCallback(async (stateId) => {
    try {
      const res = await getCitiesByStateVendor(stateId);
      if (res?.success) setCities(res.data);
    } catch (err) {
      showToast(err?.response?.data?.message, "error", true);
    }
  }, []);

  const fetchVendorsByLocation = async (location) => {
    try {
      const res = await fetchAllVendors(lobId, location.state.toUpperCase(), location.city.toUpperCase());
      setLocationLabel({ state: selectedLocation?.state, city: selectedLocation?.city })
      const vendorData = res?.data;
      const vendors = vendorData?.vendors || [];
      if (res?.success) {
        setVendorsData(vendors);
        return true;
      }

      showToast(vendorData?.message ,"success", true);
      return false;
    } catch (err) {
      showToast(
        err?.response?.data?.message || "An error occurred.",
        "error",
        true
      );
      return false;
    }
  };
  const validate = () => {
    const newErrors = {};

    if (!selectedLocation.state) newErrors.state = true;
    if (!selectedLocation.city) newErrors.city = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStateChange = (e) => {
    const value = e.target.value;
    if (value === selectedLocation.state) return;
    const selectedStateObj = states.find(
      (state) => capitalize(state.state_name) === value
    );
    const newStateId = selectedStateObj ? selectedStateObj.state_id : "";
    setSelectedLocation({
      ...initialLocationState,
      state: value,
      stateId: newStateId,
    });
    setCities([]);
    setLocationLabel({});
    setErrors((prev) => ({ ...prev, state: false }));
    if (newStateId) {
      fetchCities(newStateId);
    }
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    if (!selectedLocation.state) {
      showToast("Please select a state first.", "warning", true);
      return;
    }
    if (value === selectedLocation.city) return;
    setSelectedLocation((prev) => ({ ...prev, city: value }));
    setErrors((prev) => ({ ...prev, city: false }));
  };

  const handleSubmit = () => {
    if (!validate()) {
      showToast("Please select both state and city.", "warning", true);
      return;
    }
    fetchAllVendors(lobId, selectedLocation.state.toUpperCase(), selectedLocation.city.toUpperCase())
    .then((res) => {
        const vendorData = res?.data;
        const vendors = vendorData?.vendors || [];
        if (res?.success) {
          setVendorsData(vendors);
          showToast(`${res?.message}.`, "success", true);
          setLocationLabel({state: selectedLocation?.state, city: selectedLocation?.city})
        } else {
          showToast(vendorData?.message || res?.message || "Failed to fetch vendors.", "error", true);
        }
      })
      .catch((err) => {
        showToast(
          err?.response?.data?.message || "An error occurred.",
          "error",
          true
        );
      });
  };

  const handleCreateVendor = async (payload) => {
    if (!userRole) {
      showToast("Unable to determine user role.", "error", true);
      return { success: false };
    }
    try {
      const roleBasedPayload = {
        ...payload,
        created_by: userRole,
        updated_by: userRole,
      };
      const res = await vendorOnboard(roleBasedPayload);
      if (res?.success) {
        showToast(
          res?.data?.message || res?.message || "Vendor created successfully.",
          "success",
          true
        );
        const vendorId = res?.data?.data?.vendor_company_id ?? null;
        return { success: true, vendorId };
      }
      showToast(res?.message || "Failed to create vendor.", "error", true);
      return { success: false };
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to create vendor.",
        "error",
        true
      );
      return { success: false };
    }
  };

  const handleCreateVendorLocations = async (payload) => {
    try {
      const res = await addVendorLocation(payload);
      if (res?.success) {
        showToast(
          res?.message || "Vendor locations saved successfully.",
          "success",
          true
        );
        if (selectedLocation.state && selectedLocation.city) {
          await fetchVendorsByLocation(selectedLocation);
        }
        return true;
      }
      showToast(res?.message || "Failed to save locations.", "error", true);
      return false;
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to save locations.",
        "error",
        true
      );
      return false;
    }
  };

  const handleResetStateCity = async () => {
    dispatch(isLoadingStateReducer(true));
    await initialVendorList(lobId)
      .then((res) => {
        setVendorsData(res?.data?.vendors);
        dispatch(isLoadingStateReducer(false));
        showToast("All filters removed", "success", true);
        setSelectedLocation(initialLocationState);
      })
      .catch((err) => {
        dispatch(isLoadingStateReducer(false));
        showToast("Failed to remove filters", "error", true);
        showToast(err?.response?.data?.message, "error", true);
      })
  };

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    const decodedToken = secureData(loginToken, "string_decrypt");
    const infoFromToken = jwtDecode(decodedToken);
    setLobId(infoFromToken?.lobIds);
    dispatch(isLoadingStateReducer(true));
    if(selectedLocation.state && selectedLocation.city){
      handleSubmit();
    }
    else{
      initialVendorList(infoFromToken?.lobIds)
      .then((res) => {
        setVendorsData(res?.data?.vendors);
        dispatch(isLoadingStateReducer(false));
      })
      .catch((err) => {
        dispatch(isLoadingStateReducer(false));
        showToast(err?.response?.data?.message, "error", true);
      })
    }
  }, [selectedVendor]);

  return (
    <div>
      <Box className={styles["mainContainer"]}>
        <Box className={styles["headerContainer"]}>
          <h1 className={styles.pageTitle}>Vendor Management</h1>
          {hasReadWritePermission && (
            <CommonButton
              label="+ Add Vendor"
              onClick={() => setIsAddVendorOpen(true)}
              filledButton
              fitContent
              className={styles["addVendorBtn"]}
            />
          )}
        </Box>

        <div className={styles.formCard}>
          <p className={styles.subtitle}>Please select the location</p>
          <div className={styles.selectRow}>
            <div className={styles.dropdownContainer}>
              <p className={styles.dropdownTitle}>
                State
                <span className={styles["requiredStar"]}> *</span>
              </p>
              <div className={styles["filterSelectWrapper"]}>
                <CustomSelect
                  key={selectedLocation.state}
                  options={states.map((state) => capitalize(state.state_name))}
                  placeholder={selectedLocation.state || "select"}
                  handleChange={handleStateChange}
                  customSelectValue={selectedLocation.state}
                  value={selectedLocation.state}
                  border="0.1rem solid #B1B3B5"
                  height="3.8rem"
                  placeholderColor="#2D2D2D"
                />
              </div>
              {errors.state && (
                <p className={styles.errorText}>Please select a state.</p>
              )}
            </div>

            <div className={styles.dropdownContainer}>
              <p className={styles.dropdownTitle}>
                City
                <span className={styles["requiredStar"]}> *</span>
              </p>
              <div className={styles["filterSelectWrapper"]}>
                <CustomSelect
                  key={cities}
                  options={cities}
                  placeholder={selectedLocation.city || "select"}
                  handleChange={handleCityChange}
                  customSelectValue={selectedLocation.city}
                  value={selectedLocation.city}
                  disabled={!selectedLocation.state}
                  border="0.1rem solid #B1B3B5"
                  height="3.8rem"
                  placeholderColor="#2D2D2D"
                />
              </div>
              {errors.city && (
                <p className={styles.errorText}>Please select a city.</p>
              )}
            </div>
            <div className={`${styles.dropdownContainer} ${styles["submitBtnContainer"]}`}>
              <CommonButton
                label="Submit"
                onClick={handleSubmit}
                filledButton
                className={errors.city || errors.state ? styles["submitBtnErrState"] : styles["submitBtn"]}
              />
            </div>
            {selectedLocation.city &&
              <CommonButton
                label="Clear Filters"
                onClick={handleResetStateCity}
                disabled={!selectedLocation.city}
                className={styles["clearBtn"]}
                endIcon={<ClearIcon />}
                isEndIcon={true}

              />
            }
          </div>
            <div className={styles.vendorList}>
              {locationLabel.state && locationLabel.city ? (
                <h2 className={styles.vendorListTitle}>
                  Vendors in {selectedLocation.city}, {selectedLocation.state}
                </h2>
              ) : (
                <h2 className={styles.vendorListTitle}>
                  Recently added Vendors.
                </h2>
              )}
              {vendorsData.length > 0 ? (
                <Grid container spacing={2.5}>
                  {vendorsData.map((vendor, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                      <VendorCard
                        vendor={vendor}
                        onView={(vendor) => {
                          setSelectedVendor(vendor);
                          setIsViewVendorOpen(true);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <div className={styles["noLocationsText"]}>
                  No vendors assigned to the location.
                </div>
              )}
            </div>
        </div>
      </Box>
      <AddVendorModal
        open={isAddVendorOpen}
        selectedLocation={selectedLocation}
        employeeId={employeeId}
        userRole={userRole}
        onClose={() => setIsAddVendorOpen(false)}
        onCreateVendor={handleCreateVendor}
        onCreateVendorLocations={handleCreateVendorLocations}
      />
      <DisplayVendorDetails
        open={isViewVendorOpen}
        vendorId={selectedVendor?.vendor_company_id}
        vendor={selectedVendor}
        onClose={() => {
          setIsViewVendorOpen(false);
          setSelectedVendor(null);
        }}
        hasReadWritePermission={hasReadWritePermission}
      />
    </div>
  );
};

export default NewVendorManagement;